import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Bell,
  Building2,
  Clock,
  FileText,
  Loader2,
  Package,
  ShoppingCart,
  Truck,
  UserPlus,
} from "lucide-react";

import { PageHeader, Surface, cn, focusRing } from "../../components/ui";
import { timeAgo } from "../../utils/timeAgo";
import { useSupplierAuth } from "../context/SupplierAuthContext";
import { supplierDashboardAPI, supplierNotificationAPI } from "../services/supplierApi";

export default function SupplierDashboard() {
  const { suppliers } = useSupplierAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [summaryRes, notifRes] = await Promise.all([
          supplierDashboardAPI.summary().catch(() => null),
          supplierNotificationAPI.list({ limit: 5 }).catch(() => ({ data: [] })),
        ]);
        setSummary(summaryRes?.data || null);
        setNotifications(notifRes?.data || []);
      } catch {
        setSummary(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cards = summary
    ? [
        { label: "Connected Businesses", value: summary.connected_businesses, sub: `${summary.pending_invites} pending`, icon: Building2, to: "/supplier/businesses" },
        { label: "Active RFQs", value: summary.active_rfqs, sub: `${summary.total_rfqs} total`, icon: FileText, to: "/supplier/rfqs" },
        { label: "Active Orders", value: summary.active_orders, sub: `${summary.total_orders} total`, icon: ShoppingCart, to: "/supplier/orders" },
        { label: "Pending Deliveries", value: summary.pending_deliveries, sub: `${summary.total_deliveries} total`, icon: Truck, to: "/supplier/deliveries" },
        { label: "Mapped Products", value: summary.mapped_products, sub: "across all businesses", icon: Package, to: "/supplier/products" },
      ]
    : [];

  const attentionItems = [];
  if (summary) {
    if (summary.pending_invites > 0) {
      attentionItems.push({ icon: UserPlus, label: `${summary.pending_invites} pending business invite${summary.pending_invites > 1 ? "s" : ""}`, to: "/supplier/notifications" });
    }
    if (summary.active_rfqs > 0) {
      attentionItems.push({ icon: FileText, label: `${summary.active_rfqs} RFQ${summary.active_rfqs > 1 ? "s" : ""} awaiting your response`, to: "/supplier/rfqs" });
    }
    if (summary.pending_deliveries > 0) {
      attentionItems.push({ icon: Truck, label: `${summary.pending_deliveries} deliver${summary.pending_deliveries > 1 ? "ies" : "y"} in progress`, to: "/supplier/deliveries" });
    }
  }

  const supplierName = suppliers?.[0]?.name || "Supplier";
  const connectedBusinesses = summary?.connected_businesses ?? suppliers?.length ?? 0;

  return (
    <div className="operations-page">
      <PageHeader
        title={`Welcome back, ${supplierName}`}
        description={
          suppliers?.length
            ? `${supplierName} · Linked to ${connectedBusinesses} business${connectedBusinesses > 1 ? "es" : ""}`
            : "No business links yet"
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-16" role="status" aria-label="Loading dashboard">
          <Loader2 className="h-5 w-5 animate-spin text-accent" />
        </div>
      ) : summary ? (
        <>
          <section className="grid grid-cols-2 gap-3 xl:grid-cols-5" aria-label="Supplier summary">
            {cards.map((card, index) => (
              <Surface
                as="button"
                key={card.label}
                type="button"
                onClick={() => navigate(card.to)}
                className={cn(
                  "group min-h-20 p-3 text-left transition-colors hover:border-strong hover:bg-hover sm:p-4",
                  index === cards.length - 1 && "col-span-2 xl:col-span-1",
                  focusRing
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-meta font-medium text-secondary">{card.label}</p>
                    <p className="mt-1.5 text-metric tabular-nums text-foreground">{card.value}</p>
                    <p className="mt-0.5 truncate text-meta text-tertiary">{card.sub}</p>
                  </div>
                  <card.icon className="mt-0.5 h-4 w-4 shrink-0 text-tertiary transition-colors group-hover:text-accent" aria-hidden="true" />
                </div>
              </Surface>
            ))}
          </section>

          <div className="grid min-h-0 items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]">
            <Surface className="overflow-hidden">
              <div className="flex items-center justify-between gap-3 border-b border-subtle px-4 py-3">
                <h2 className="flex items-center gap-2 text-panel-title text-foreground">
                  <Bell className="h-4 w-4 text-tertiary" aria-hidden="true" />
                  Recent Activity
                </h2>
                <button
                  type="button"
                  onClick={() => navigate("/supplier/notifications")}
                  className={cn("rounded-control text-body-sm font-medium text-accent hover:text-accent-hover", focusRing)}
                >
                  View all
                </button>
              </div>

              {notifications.length === 0 ? (
                <p className="px-4 py-8 text-body-sm text-secondary">No recent activity.</p>
              ) : (
                <div className="divide-y divide-subtle">
                  {notifications.map((notification) => (
                    <div key={notification.id} className="flex items-start gap-3 px-4 py-3 hover:bg-hover">
                      <span
                        className={cn(
                          "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
                          notification.is_read ? "bg-surface-sunken" : "bg-accent"
                        )}
                        aria-hidden="true"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-body-sm font-medium text-foreground">{notification.title || "Notification"}</p>
                        {notification.body ? <p className="mt-0.5 truncate text-meta text-secondary">{notification.body}</p> : null}
                      </div>
                      <span className="flex shrink-0 items-center gap-1 text-meta text-tertiary">
                        <Clock className="h-3 w-3" aria-hidden="true" />
                        {timeAgo(notification.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Surface>

            <div className="flex flex-col gap-4">
              {attentionItems.length > 0 ? (
                <Surface className="overflow-hidden">
                  <div className="flex items-center gap-2 border-b border-subtle px-4 py-3">
                    <AlertCircle className="h-4 w-4 text-warning" aria-hidden="true" />
                    <h2 className="text-panel-title text-foreground">Needs Attention</h2>
                  </div>
                  <div className="divide-y divide-subtle">
                    {attentionItems.map((item) => (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => navigate(item.to)}
                        className={cn("flex w-full items-center gap-2 px-4 py-3 text-left text-body-sm text-secondary hover:bg-hover hover:text-foreground", focusRing)}
                      >
                        <item.icon className="h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
                        <span className="min-w-0 flex-1">{item.label}</span>
                        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-tertiary" aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                </Surface>
              ) : null}

              <Surface className="p-4">
                <h2 className="text-panel-title text-foreground">How it works</h2>
                <ol className="mt-3 list-inside list-decimal space-y-2 text-body-sm text-secondary">
                  <li><strong className="font-medium text-foreground">Businesses</strong> — View and manage your connected businesses and their mapped products.</li>
                  <li><strong className="font-medium text-foreground">RFQs</strong> — Review requests and submit your best quote (price, quantity, delivery time).</li>
                  <li><strong className="font-medium text-foreground">Orders</strong> — Once a PO is issued, acknowledge it to confirm you'll fulfill the order.</li>
                  <li><strong className="font-medium text-foreground">Deliveries</strong> — Update shipping status and tracking info as you ship.</li>
                </ol>
              </Surface>
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2 rounded-panel border border-warning bg-warning-subtle p-4 text-body-sm text-warning">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          Could not load dashboard data. Try refreshing the page.
        </div>
      )}
    </div>
  );
}
